import {LocalizedSpineButton} from '@fantasmagames/core/src/display/LocalizedSpineButton';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {SpineAnimationMultiTrack} from '@fantasmagames/core/src/display/SpineAnimationMultiTrack';
import {SpineButton} from '@fantasmagames/core/src/display/SpineButton';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {WinNumbers} from '@fantasmagames/slot/src/modules/winpresentation/WinNumbers';
import {Spine} from '@fantasmagames/core/src/spine/Spine';

export class FreeSpinsView extends View {
	public static readonly FREE_SPINS_INTRO_FINISHED: string = 'FreeSpinsView::FREE_SPINS_INTRO_FINISHED';
	public static readonly FREE_SPINS_INFO_CONTINUE_CLICKED: string = 'FreeSpinsView::FREE_SPINS_INFO_CONTINUE_CLICKED';
	public static readonly FREE_SPINS_INTRO_TRANSITION_FINISHED: string = 'FreeSpinsView::FREE_SPINS_INTRO_TRANSITION_FINISHED';

	public static readonly FREE_SPINS_OUTRO_STARTED: string = 'FreeSpinsView::FREE_SPINS_OUTRO_STARTED';
	public static readonly FREE_SPINS_OUTRO_CONTINUE_CLICKED: string = 'FreeSpinsView::FREE_SPINS_OUTRO_CONTINUE_CLICKED';
	public static readonly FREE_SPINS_OUTRO_FINISHED: string = 'FreeSpinsView::FREE_SPINS_OUTRO_FINISHED';

	public static readonly SHOWING_WIN_SUMMARY_SCREEN: string = 'FreeSpinsView::SHOWING_WIN_SUMMARY_SCREEN';

	public static readonly RETRIGGER_STARTED: string = 'FreeSpinsView::RETRIGGER_STARTED';
	public static readonly RETRIGGER_FINISHED: string = 'FreeSpinsView::RETRIGGER_FINISHED';

	public static readonly FONT_NAME_MOBILE: string = 'MED_';
	public static readonly FONT_NAME_DESKTOP: string = 'MED_';

	////////////////////////////////////////////////////////////////////
	// Intro Screens
	////////////////////////////////////////////////////////////////////
	protected _freeSpins: number;

	protected _playButton: LocalizedSpineButton;

	protected _currentFeatureAnimation: SpineAnimationMultiTrack;
	protected _currentIntroScreen: SpineAnimation;

	protected _nextFeatureButton: SpineButton;
	protected _prevFeatureButton: SpineButton;

	protected _currentFeature: number = 1;

	protected _featureTextStyle: PIXI.TextStyle;

	protected _totalFeatures: number;

	////////////////////////////////////////////////////////////////////
	// OutroScreen
	////////////////////////////////////////////////////////////////////
	protected _winSummaryScreenAnimation: SpineAnimation;
	protected _exitButton: SpineButton;
	////////////////////////////////////////////////////////////////////

	// free spins retrigger
	public showAddedFreespins(addedFreeSpins: number): void {
		const additionalFreespins: SpineAnimation = this.spineUtils
			.startSpineAnimationCreation('FREESPIN_ADDITIONAL_FREESPINS.json', 'ENTER', false)
			.onComplete( () => {
				this.emit(FreeSpinsView.RETRIGGER_FINISHED);
			})
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.createAndStart();

		this.spineUtils.replaceElementInSpineAnimation(
			additionalFreespins,
			'TEXT',
			new PIXI.Sprite(this.resources.getLocaleTextImage('EXTRA_FREE_SPINS.png')),
		);

		this.emit(FreeSpinsView.RETRIGGER_STARTED);
	}

	public startFreeSpinsIntro(): void {
		this._currentFeature = 1;
		this.killCurrentAnimation();

		this._currentIntroScreen = this.spineUtils
			.startSpineAnimationCreation('FREESPIN_STARTSCREEN.json', 'START', false)
			.chainIdleAnimation('IDLE')
			.onComplete(() => {
				this.addInfo();
			})
			.createAndStart();

		this.replacePlayButtonWithLocalized();
	}

	public startFreeSpinsRestoredIntro(): void {
		this.killCurrentAnimation();

		this._currentIntroScreen = this.spineUtils
			.startSpineAnimationCreation('FREESPIN_STARTSCREEN.json', 'IDLE', true)
			.container(this.container)
			.createAndStart();

		this.replacePlayButtonWithLocalized();
		this.addInfo();
	}

	protected replacePlayButtonWithLocalized(): void {
		this._playButton = this.spineUtils.createLocalizedSpineButton(
			'PLAY_BUTTON.json',
			this.onStartButtonClicked.bind(this),
			['PLAY_BUTTON.png', 'PLAY_BUTTON_CLICKED.png', 'PLAY_BUTTON_HOVER.png', 'PLAY_BUTTON_DISABLED.png'],
			['BUTTON', 'PLAY_BUTTON_CLICKED', 'PLAY_BUTTON_HOVER', 'PLAY_BUTTON_DISABLED'],
			true,
			false,
			false,
			false,
		);

		this.spineUtils.replaceElementInSpineAnimation(this._currentIntroScreen, 'PLAY_BUTTON', this._playButton);
		this._playButton.disable();
	}

	public setFreeSpinsAmount(amount: number): void {
		this._freeSpins = amount;
	}

	public gotoPreviousFeature(): void {
		if (this._prevFeatureButton) {
			this.onLeftButtonClicked();
		}
	}

	public gotoNextFeature(): void {
		if (this._nextFeatureButton) {
			this.onRightButtonClicked();
		}
	}

	public proceed(): void {
		if (this._playButton && this._playButton.interactive) {
			this.onStartButtonClicked();
		}
	}

	public proceedToFreeSpins(): void {
		this.emit(FreeSpinsView.FREE_SPINS_INTRO_TRANSITION_FINISHED);
	}

	public startFreeSpinsOutro(totalWin: number): void {
		this.emit(FreeSpinsView.FREE_SPINS_OUTRO_STARTED);
	}

	protected getLocaleTextImage(name: string): PIXI.Sprite {
		return new PIXI.Sprite(this.resources.getLocaleTextImage(`${name}.png`));
	}

	public showWinSummaryScreen(totalWin: number, maximumSizeInPixels?: number): void {
		this.killWinSummaryScreen();

		if (totalWin === 0) {
			this._winSummaryScreenAnimation = this.spineUtils
				.startSpineAnimationCreation('FREESPIN_COMPLETE_SCREEN.json', 'START', false)
				.chainIdleAnimation('IDLE')
				.onComplete(() => {
					this.emit(FreeSpinsView.SHOWING_WIN_SUMMARY_SCREEN);
				})
				.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
				.container(this.container)
				.createAndStart();

			const textImage: PIXI.Sprite = this.getLocaleTextImage('FREESPIN_COMPLETE');

			this.spineUtils.replaceElementInSpineAnimation(this._winSummaryScreenAnimation, 'TEXT', textImage);
			this.adjustWinScreenLocalizedScale(textImage, true);
		} else {
			this._winSummaryScreenAnimation = this.spineUtils
				.startSpineAnimationCreation('WIN_SCREEN.json', 'START', false)
				.chainIdleAnimation('IDLE')
				.onComplete(() => {
					this.emit(FreeSpinsView.SHOWING_WIN_SUMMARY_SCREEN);
					this.fitElementIntoScreen(winAmount);
				})
				.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
				.container(this.container)
				.createAndStart();

			const textImage: PIXI.Sprite = this.getLocaleTextImage('WIN_SCREEN');
			this.spineUtils.replaceElementInSpineAnimation(this._winSummaryScreenAnimation, 'TEXT', textImage);
			this.adjustWinScreenLocalizedScale(textImage, false);

			const winAmount: WinNumbers = new WinNumbers(this.resources, this.getWinnumberSize(this.device.isMobile));

			winAmount.currencyISO = this.currencyFormatter.currency;
			winAmount.currencySign = this.currencyFormatter.currencySign;
			winAmount.numberAnchor.y = 0.5;
			winAmount.numberKerning = -10;
			winAmount.showNumber(this.currencyFormatter.formatCurrencyApi(totalWin));

			this.spineUtils.replaceElementInSpineAnimation(this._winSummaryScreenAnimation, 'NUMBER', winAmount);
			this.fitElementIntoScreen(winAmount);
		}

		this._exitButton = this.spineUtils.createSpineButton(
			'FREESPIN_BUTTON.json',
			this.onExitWinSummaryButtonClicked.bind(this),
			true,
			false,
			false,
			false,
		);

		const playPosition: Position = this.spineUtils.getBoneAbsolutePosition(
			this.resources.getSpine('FREESPIN_STARTSCREEN.json'),
			'PLAY_BUTTON',
		);
		playPosition.y *= -1;

		this.container.addChild(this._exitButton);
		this._exitButton.position.set(playPosition.x, playPosition.y);
		this.soundPlayer.play('freeSpinsExit');
	}

	protected adjustWinScreenLocalizedScale(textImage: PIXI.Sprite, isNoWin: boolean): void {}

	protected getWinnumberSize(isMobile: boolean): string {
		return isMobile ? FreeSpinsView.FONT_NAME_MOBILE : FreeSpinsView.FONT_NAME_DESKTOP;
	}

	protected get numberElementLimit(): number {
		return this.resolutionManager.resolution.width * 0.8;
	}

	protected fitElementIntoScreen(element: PIXI.Sprite): void {
		element.scale.set(1, -1);
		const currentWidth: number = element.getBounds().width;

		if (currentWidth > this.numberElementLimit) {
			const currentScale: Position = element.scale;
			const scaleFactor: number = this.numberElementLimit / currentWidth;

			const xScale = currentScale.x * scaleFactor;
			const yScale = -1 * Math.abs(currentScale.y) * scaleFactor;

			element.scale.set(xScale, yScale);
		}
	}

	protected killCurrentAnimation(): void {
		if (this._currentIntroScreen) {
			this.spineUtils.stopAndDestroyAnimation(this._currentIntroScreen);
			this._currentIntroScreen = null;
		}
	}

	public enablePlayButton(): void {
		this._playButton.enable();
	}

	protected addInfo(): void {
		this._currentFeatureAnimation = this.spineUtils
			.startSpineAnimationCreation('FREESPIN_FEATURE_SCREENS.json', 'FEATURE_1_ENTRY', false)
			.container(this.container)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.onComplete(() => {
				this.emit(FreeSpinsView.FREE_SPINS_INTRO_FINISHED);
				this.setupActiveInfoElements();
			})
			.createMultitrack();

		this.replaceInfoElementComponents();
	}

	protected setupActiveInfoElements(): void {
		this.setupInput();
		this.spineUtils.stopAndDestroyAnimation(this._currentFeatureAnimation);

		this._currentFeatureAnimation = this.spineUtils
			.startSpineAnimationCreation('FREESPIN_FEATURE_SCREENS.json', 'FEATURE_1_IDLE', false)
			.container(this.container)
			.position(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height)
			.createMultitrack();

		this.playIdleAnimation();

		this.setupTextStyle();
		this.replaceInfoElementComponents();
	}

	protected adjustTitleScale(currentPage: number, currentTitle: number, title: PIXI.Sprite): void {
		// E.g if currentPage === 1 and currentTitle = 4 and device is mobile. Set scale to 0.5.
	}

	protected replaceInfoElementComponents(): void {
		let hasMorePages: boolean = true;
		let currentPage: number = 1;
		while (hasMorePages) {
			hasMorePages = !!this.spineUtils.findSlotContainerByName(
				this._currentFeatureAnimation.container as Spine,
				`FREE_SPINS_FEATURE_${currentPage}_1_TITLE`,
			);
			if (hasMorePages) {
				let hasMoreTitles: boolean = true;
				let currentTitle: number = 1;
				while (hasMoreTitles) {
					hasMoreTitles = !!this.spineUtils.findSlotContainerByName(
						this._currentFeatureAnimation.container as Spine,
						`FREE_SPINS_FEATURE_${currentPage}_${currentTitle}_TITLE`,
					);
					if (hasMoreTitles) {
						const title: PIXI.Sprite = new PIXI.Sprite(
							this.resources.getLocaleTextImage(`FREE_SPINS_FEATURE_${currentPage}_${currentTitle}_TITLE.png`),
						);

						this.spineUtils.replaceElementInSpineMultiTrack(
							this._currentFeatureAnimation,
							`FREE_SPINS_FEATURE_${currentPage}_${currentTitle}_TITLE`,
							title,
						);
						this.adjustTitleScale(currentPage, currentTitle, title);
					}
					currentTitle++;
				}
			}
			currentPage++;
		}

		this._totalFeatures = currentPage - 2;

		for (let i = 1; i <= currentPage; i++) {
			const hasTextContainer: boolean = !!this.spineUtils.findSlotContainerByName(
				this._currentFeatureAnimation.container as Spine,
				`FEATURE_${i}_TEXT`,
			);
			if (hasTextContainer) {
				this.spineUtils.replaceElementInSpineMultiTrack(
					this._currentFeatureAnimation,
					`FEATURE_${i}_TEXT`,
					this.createTranslatedTextObject(this.translator.getObject('BaseStrings', 'StartScreen', `page${i}`, 'text')),
				);
			}
		}
	}

	protected createTranslatedTextObject(text: string): PIXI.Text {
		const textObject = new PIXI.Text(text, this._featureTextStyle);

		return textObject;
	}

	protected setupTextStyle(): void {
		const featureBackgroundReference = this.spineUtils.getSlotAttachmentDimensions(
			this.spineUtils.getSkeletonDataFromSpineAnimationMultiTrack(this._currentFeatureAnimation),
			'TEXT_AREA',
		);

		const descriptionWordWrapWidth = featureBackgroundReference.width * featureBackgroundReference.scale.x;

		this._featureTextStyle = this.textStyles.featureTextStyle;
		this._featureTextStyle.wordWrapWidth = descriptionWordWrapWidth;
	}

	protected translateIncreasingMultiplierScreen(): PIXI.Text {
		const text = this.translator.getObject('BaseStrings', 'Freespin_StartScreen', 'page1', 'text');
		const paragraph = new PIXI.Text(text, this.textStyles.featureTextStyle);

		return paragraph;
	}

	protected setupInput(): void {
		this._prevFeatureButton = this.spineUtils.createSpineButton(
			'ARROW_BUTTON.json',
			this.onLeftButtonClicked.bind(this),
			true,
			false,
			false,
			false,
		);
		this.spineUtils.replaceElementInSpineAnimation(this._currentIntroScreen, 'ARROW_LEFT', this._prevFeatureButton);

		this._nextFeatureButton = this.spineUtils.createSpineButton(
			'ARROW_BUTTON.json',
			this.onRightButtonClicked.bind(this),
			true,
			false,
			false,
			false,
		);
		this.spineUtils.replaceElementInSpineAnimation(this._currentIntroScreen, 'ARROW_RIGHT', this._nextFeatureButton);

		if (this.device.isMobile) {
			this._nextFeatureButton.setOnClickIsOverState(false);
			this._prevFeatureButton.setOnClickIsOverState(false);
		}

		this._playButton = this.spineUtils.createLocalizedSpineButton(
			'PLAY_BUTTON.json',
			this.onStartButtonClicked.bind(this),
			['PLAY_BUTTON.png', 'PLAY_BUTTON_CLICKED.png', 'PLAY_BUTTON_HOVER.png', 'PLAY_BUTTON_DISABLED.png'],
			['BUTTON', 'PLAY_BUTTON_CLICKED', 'PLAY_BUTTON_HOVER', 'PLAY_BUTTON_DISABLED'],
			true,
			false,
			false,
			false,
		);

		this.spineUtils.replaceElementInSpineAnimation(this._currentIntroScreen, 'PLAY_BUTTON', this._playButton);
		// this._playButton.disable();
	}

	protected playIdleAnimation(): void {
		this._currentFeatureAnimation.play(`FEATURE_${this._currentFeature}_IDLE`, 0, true);
	}

	protected onLeftButtonClicked(): void {
		if (this._prevFeatureButton.interactive) {
			this.soundPlayer.play('buttonClick');

			this._currentFeature--;
			if (this._currentFeature <= 0) {
				this._currentFeature = this._totalFeatures;
			}
			this._currentFeatureAnimation.play(`FEATURE_${this._currentFeature}_ENTER_LEFT`, 0, false);
			this._currentFeatureAnimation.setOnceOnComplete(this.playIdleAnimation.bind(this));
		}
	}

	protected onRightButtonClicked(): void {
		if (this._prevFeatureButton.interactive) {
			this.soundPlayer.play('buttonClick');

			this._currentFeature++;
			if (this._currentFeature > this._totalFeatures) {
				this._currentFeature = 1;
			}
			this._currentFeatureAnimation.play(`FEATURE_${this._currentFeature}_ENTER_RIGHT`, 0, false);
			this._currentFeatureAnimation.setOnceOnComplete(this.playIdleAnimation.bind(this));
		}
	}

	protected onStartButtonClicked(): void {
		this._playButton.disable();

		this.soundPlayer.play('buttonClick');

		if (this._nextFeatureButton) {
			this._nextFeatureButton.disable();
		}
		if (this._prevFeatureButton) {
			this._prevFeatureButton.disable();
		}

		this.emit(FreeSpinsView.FREE_SPINS_INFO_CONTINUE_CLICKED);
	}

	public endFreeSpinsIntro(): void {
		this.killCurrentFeatureAnimation();

		this._currentIntroScreen.changeAnimationTo('EXIT', false);
		this._currentIntroScreen.setOnComplete(() => {
			this.cleanupIntro();
			this.proceedToFreeSpins();
		});
	}

	protected killCurrentFeatureAnimation(): void {
		this.spineUtils.stopAndDestroyAnimation(this._currentFeatureAnimation);
		this._currentFeatureAnimation = null;
	}

	protected cleanupIntro(): void {
		this.spineUtils.stopAndDestroyAnimation(this._currentIntroScreen);
	}

	protected onExitWinSummaryButtonClicked(): void {
		this.emit(FreeSpinsView.FREE_SPINS_OUTRO_CONTINUE_CLICKED);
		this._exitButton.destroy();

		this._winSummaryScreenAnimation.changeAnimationTo('EXIT', false);
		this._winSummaryScreenAnimation.setOnComplete(() => {
			this.killWinSummaryScreen();
			this.emit(FreeSpinsView.FREE_SPINS_OUTRO_FINISHED);
		});
	}

	protected killWinSummaryScreen(): void {
		this.spineUtils.stopAndDestroyAnimation(this._winSummaryScreenAnimation);
		this._winSummaryScreenAnimation = null;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsView.ts